import React from 'react';
import {
  ERROR_MESSAGES,
  FIELD_LIMITS,
  PRIVACY_CONTACT_EMAIL,
  TERMLY_DATA_REQUEST_URL,
  retryAfterText,
  submitDeletionRequest,
  validateDeletionRequest,
} from '../../service/accountDeletion';
import { StyledParagraph } from '../Blog/styled';
import {
  StyledAlert,
  StyledError,
  StyledField,
  StyledForm,
  StyledHint,
  StyledHoneypot,
  StyledLinkButton,
  StyledPanel,
  StyledSubmit,
} from './styled';

const EMPTY = { email: '', discordName: '', clubName: '', reason: '' };
// Backend fields whose message differs from the form field's own message.
const SERVER_FIELD_MESSAGES = {
  email_or_discord_name: ERROR_MESSAGES.identifier,
  body: ERROR_MESSAGES.body,
};

const TermlyLink = ({ children }) => (
  <a href={TERMLY_DATA_REQUEST_URL} target="_blank" rel="noopener noreferrer">
    {children}
  </a>
);

const MailLink = () => <a href={`mailto:${PRIVACY_CONTACT_EMAIL}`}>{PRIVACY_CONTACT_EMAIL}</a>;

function Field({ id, label, hint, error, children }) {
  return (
    <StyledField invalid={Boolean(error)}>
      <label htmlFor={id}>{label}</label>
      {children}
      {error ? (
        <StyledError id={`${id}-error`} role="alert">
          {error}
        </StyledError>
      ) : (
        <StyledHint id={`${id}-hint`}>{hint}</StyledHint>
      )}
    </StyledField>
  );
}

/** Form-level failure (not tied to a field): Termly is the prominent way out. */
function FailureAlert({ failure }) {
  if (failure.kind === 'rate_limited') {
    return (
      <StyledAlert tone="error" role="alert" data-testid="delete-account-rate-limited">
        Too many requests from your connection. Please try again{' '}
        {retryAfterText(failure.retryAfter)}, or use our{' '}
        <TermlyLink>privacy request form</TermlyLink>.
      </StyledAlert>
    );
  }
  return (
    <StyledAlert tone="error" role="alert" data-testid="delete-account-error">
      We couldn’t send your request just now. Please use our privacy request
      form instead, or email <MailLink /> from the address on your account.
      <br />
      <StyledLinkButton href={TERMLY_DATA_REQUEST_URL} target="_blank" rel="noopener noreferrer">
        Open the privacy request form
      </StyledLinkButton>
    </StyledAlert>
  );
}

/**
 * Deletion request form. Posts to `endpoint` (backend PR #167); the Termly data
 * request form stays available as a secondary link and as the error fallback.
 */
export default function DeleteAccountForm({ endpoint, submit = submitDeletionRequest }) {
  const [values, setValues] = React.useState(EMPTY);
  const [errors, setErrors] = React.useState({});
  const [status, setStatus] = React.useState('idle'); // idle | loading | success | error
  const [failure, setFailure] = React.useState(null);
  const [requestId, setRequestId] = React.useState(null);

  if (status === 'success') {
    return (
      <StyledPanel>
        <StyledAlert tone="success" role="status" data-testid="delete-account-success">
          <strong>Request received.</strong> We’ll check that the account is
          yours and contact you before we delete it.
          {requestId ? (
            <>
              <br />
              Your request ID is <strong data-testid="delete-account-request-id">{requestId}</strong>.
              Keep it if you contact us about this request.
            </>
          ) : null}
        </StyledAlert>
      </StyledPanel>
    );
  }

  const onChange = (field) => (event) => {
    const { value } = event.target;
    setValues((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => {
      if (!prev[field]) return prev;
      const next = { ...prev };
      delete next[field];
      // "email or Discord name" is one shared error: typing in either clears both.
      if (prev[field] === ERROR_MESSAGES.identifier) {
        if (next.email === ERROR_MESSAGES.identifier) delete next.email;
        if (next.discordName === ERROR_MESSAGES.identifier) delete next.discordName;
      }
      return next;
    });
  };

  const onSubmit = async (event) => {
    event.preventDefault();
    if (status === 'loading') return;
    const found = validateDeletionRequest(values);
    setErrors(found);
    setFailure(null);
    if (Object.keys(found).length) {
      setStatus('idle');
      return;
    }
    setStatus('loading');
    try {
      const result = await submit(endpoint, values);
      setRequestId((result && result.requestId) || null);
      setStatus('success');
    } catch (e) {
      if (e && e.kind === 'invalid_input' && e.fields && e.fields.length) {
        const serverErrors = {};
        e.fields.forEach((field) => {
          serverErrors[field] = SERVER_FIELD_MESSAGES[e.field] || ERROR_MESSAGES[field];
        });
        setErrors(serverErrors);
        setStatus('idle');
        return;
      }
      setFailure({ kind: (e && e.kind) || 'unavailable', retryAfter: e && e.retryAfter });
      setStatus('error');
    }
  };

  const loading = status === 'loading';
  const describedBy = (id) => (errors[id] ? `${id}-error` : `${id}-hint`);
  const inputProps = (id) => ({
    id,
    name: id,
    value: values[id],
    onChange: onChange(id),
    'aria-invalid': Boolean(errors[id]),
    'aria-describedby': describedBy(id),
    disabled: loading,
  });

  return (
    <StyledPanel>
      {failure ? <FailureAlert failure={failure} /> : null}
      <StyledForm noValidate onSubmit={onSubmit} aria-busy={loading}>
        <Field
          id="email"
          label="Account email"
          hint="The email on your account. Fill in this, your Discord name, or both."
          error={errors.email}
        >
          <input type="email" autoComplete="email" maxLength={FIELD_LIMITS.email} {...inputProps('email')} />
        </Field>
        <Field
          id="discordName"
          label="Discord name"
          hint="The Discord username you log in with."
          error={errors.discordName}
        >
          <input
            type="text"
            autoComplete="username"
            maxLength={FIELD_LIMITS.discordName + 6}
            {...inputProps('discordName')}
          />
        </Field>
        <Field
          id="clubName"
          label="Club name (optional)"
          hint="The club you manage, if you have one. It helps us find your account."
          error={errors.clubName}
        >
          <input type="text" maxLength={FIELD_LIMITS.clubName} {...inputProps('clubName')} />
        </Field>
        <Field
          id="reason"
          label="Reason (optional)"
          hint="Anything you want to tell us. Up to 1000 characters."
          error={errors.reason}
        >
          <textarea maxLength={FIELD_LIMITS.reason} {...inputProps('reason')} />
        </Field>
        {/* Bot trap: hidden from people and assistive tech; always sent empty. */}
        <StyledHoneypot aria-hidden="true">
          <label htmlFor="website">
            Website
            <input
              id="website"
              name="website"
              type="text"
              tabIndex={-1}
              autoComplete="off"
              value=""
              readOnly
            />
          </label>
        </StyledHoneypot>
        <StyledSubmit type="submit" disabled={loading}>
          {loading ? 'Sending request…' : 'Request account deletion'}
        </StyledSubmit>
      </StyledForm>
      <StyledParagraph small data-testid="delete-account-termly-secondary">
        Or use our <TermlyLink>privacy request form</TermlyLink>.
      </StyledParagraph>
    </StyledPanel>
  );
}
